import React, { useState } from "react";
import { submitFeedback } from "../../services/feedbackService";
import "./FeedbackModal.scss";

/**
 * Feedback Modal Component
 * Allows players to submit feedback with comments, bug reports, and suggestions
 */
function FeedbackModal({ isOpen, onClose, assignment, onSuccess }) {
  const [comments, setComments] = useState("");
  const [bugReports, setBugReports] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  // Removed feedback check - allow multiple submissions

  // Add bug report
  const addBugReport = () => {
    setBugReports([
      ...bugReports,
      { title: "", description: "", severity: "minor" },
    ]);
  };

  // Update bug report
  const updateBugReport = (index, field, value) => {
    const updated = [...bugReports];
    updated[index][field] = value;
    setBugReports(updated);
  };

  // Remove bug report
  const removeBugReport = (index) => {
    setBugReports(bugReports.filter((_, i) => i !== index));
  };

  // Add suggestion
  const addSuggestion = () => {
    setSuggestions([
      ...suggestions,
      { suggestion: "", category: "other" },
    ]);
  };

  // Update suggestion
  const updateSuggestion = (index, field, value) => {
    const updated = [...suggestions];
    updated[index][field] = value;
    setSuggestions(updated);
  };

  // Remove suggestion
  const removeSuggestion = (index) => {
    setSuggestions(suggestions.filter((_, i) => i !== index));
  };

  // Validate form
  const validateForm = () => {
    const hasComments = comments.trim().length > 0;
    const hasBugs = bugReports.some(
      (bug) => bug.title.trim().length > 0 && bug.description.trim().length > 0
    );
    const hasSuggestions = suggestions.some(
      (suggestion) => suggestion.suggestion.trim().length > 0
    );

    return hasComments || hasBugs || hasSuggestions;
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      setError("Please provide at least one of: comments, bug reports, or suggestions.");
      return;
    }

    setLoading(true);

    try {
      // Filter out empty bug reports and suggestions
      const validBugReports = bugReports.filter(
        (bug) => bug.title.trim().length > 0 && bug.description.trim().length > 0
      );
      const validSuggestions = suggestions.filter(
        (suggestion) => suggestion.suggestion.trim().length > 0
      );

      // Build feedback data object, only including fields that have values
      const feedbackData = {
        gameId: assignment.gameId,
        assignmentId: assignment.assignmentId || assignment._id,
      };

      // Only add fields that have values (don't send null or empty arrays)
      if (comments && comments.trim().length > 0) {
        feedbackData.comments = comments.trim();
      }
      if (validBugReports && validBugReports.length > 0) {
        feedbackData.bugReports = validBugReports;
      }
      if (validSuggestions && validSuggestions.length > 0) {
        feedbackData.suggestions = validSuggestions;
      }

      // Remove any undefined/null values to ensure clean payload
      Object.keys(feedbackData).forEach(key => {
        if (feedbackData[key] === undefined || feedbackData[key] === null) {
          delete feedbackData[key];
        }
      });

      const response = await submitFeedback(feedbackData);

      if (response.success) {
        setSuccess(true);
        setError(null);
        if (onSuccess) {
          onSuccess(response);
        }
        // Reset form but keep modal open to allow additional feedback
        setComments("");
        setBugReports([]);
        setSuggestions([]);
        // Clear success message after 5 seconds
        setTimeout(() => {
          setSuccess(false);
        }, 5000);
      }
    } catch (err) {
      // Handle backend error about already submitted feedback
      const errorMessage = err.response?.data?.message || err.message || "";
      const statusCode = err.response?.status;
      
      // Check if it's a duplicate/already submitted error
      if (statusCode === 400 || statusCode === 409 || 
          errorMessage.toLowerCase().includes("already submitted") ||
          errorMessage.toLowerCase().includes("duplicate")) {
        // Backend validation is blocking multiple submissions
        // Show the actual backend error message
        setError(`⚠️ ${errorMessage}`);
      } else {
        setError(
          errorMessage || 
          "Failed to submit feedback. Please try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle close
  const handleClose = () => {
    setError(null);
    setComments("");
    setBugReports([]);
    setSuggestions([]);
    onClose();
  };

  if (!isOpen || !assignment) {
    return null;
  }

  return (
    <div className="feedback-modal-overlay" onClick={handleClose}>
      <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
        <div className="feedback-modal-header">
          <h2>Submit Feedback for {assignment.gameName || "Game"}</h2>
          <button className="feedback-modal-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="feedback-modal-content">
            <form onSubmit={handleSubmit} className="feedback-form">
              {success && (
                <div className="feedback-success" style={{ 
                  background: 'rgba(16, 185, 129, 0.1)', 
                  border: '1px solid rgba(16, 185, 129, 0.3)', 
                  borderRadius: '8px', 
                  padding: '12px', 
                  marginBottom: '16px',
                  color: '#10b981',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <span>✓</span>
                  <span>Feedback submitted successfully! You can submit additional feedback if needed.</span>
                </div>
              )}
              {error && (
                <div className="feedback-error">
                  <span className="error-icon">⚠️</span>
                  <span>{error}</span>
                </div>
              )}

              {/* Comments Section */}
              <div className="feedback-section">
                <label htmlFor="comments" className="feedback-label">
                  General Comments <span className="optional">(optional)</span>
                </label>
                <textarea
                  id="comments"
                  className="feedback-textarea"
                  rows="4"
                  placeholder="Share your overall thoughts about the game..."
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                />
              </div>

              {/* Bug Reports Section */}
              <div className="feedback-section">
                <div className="feedback-section-header">
                  <label className="feedback-label">
                    Bug Reports <span className="optional">(optional)</span>
                  </label>
                  <button
                    type="button"
                    className="btn btn-secondary btn-small"
                    onClick={addBugReport}
                  >
                    + Add Bug Report
                  </button>
                </div>

                {bugReports.length === 0 ? (
                  <p className="feedback-hint">No bug reports added yet.</p>
                ) : (
                  bugReports.map((bug, index) => (
                    <div key={index} className="feedback-item-card">
                      <div className="feedback-item-header">
                        <input
                          type="text"
                          className="feedback-input"
                          placeholder="Bug title"
                          value={bug.title}
                          onChange={(e) =>
                            updateBugReport(index, "title", e.target.value)
                          }
                        />
                        <select
                          className="feedback-select"
                          value={bug.severity}
                          onChange={(e) =>
                            updateBugReport(index, "severity", e.target.value)
                          }
                        >
                          <option value="minor">Minor</option>
                          <option value="major">Major</option>
                          <option value="critical">Critical</option>
                        </select>
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => removeBugReport(index)}
                          aria-label="Remove bug report"
                        >
                          ×
                        </button>
                      </div>
                      <textarea
                        className="feedback-textarea"
                        rows="2"
                        placeholder="Describe the bug in detail..."
                        value={bug.description}
                        onChange={(e) =>
                          updateBugReport(index, "description", e.target.value)
                        }
                      />
                    </div>
                  ))
                )}
              </div>

              {/* Suggestions Section */}
              <div className="feedback-section">
                <div className="feedback-section-header">
                  <label className="feedback-label">
                    Suggestions <span className="optional">(optional)</span>
                  </label>
                  <button
                    type="button"
                    className="btn btn-secondary btn-small"
                    onClick={addSuggestion}
                  >
                    + Add Suggestion
                  </button>
                </div>

                {suggestions.length === 0 ? (
                  <p className="feedback-hint">No suggestions added yet.</p>
                ) : (
                  suggestions.map((suggestion, index) => (
                    <div key={index} className="feedback-item-card">
                      <div className="feedback-item-header">
                        <input
                          type="text"
                          className="feedback-input"
                          placeholder="Your suggestion"
                          value={suggestion.suggestion}
                          onChange={(e) =>
                            updateSuggestion(index, "suggestion", e.target.value)
                          }
                        />
                        <select
                          className="feedback-select"
                          value={suggestion.category}
                          onChange={(e) =>
                            updateSuggestion(index, "category", e.target.value)
                          }
                        >
                          <option value="gameplay">Gameplay</option>
                          <option value="ui">UI/UX</option>
                          <option value="performance">Performance</option>
                          <option value="other">Other</option>
                        </select>
                        <button
                          type="button"
                          className="btn-remove"
                          onClick={() => removeSuggestion(index)}
                          aria-label="Remove suggestion"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Submit Button */}
              <div className="feedback-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading || !validateForm()}
                >
                  {loading ? "Submitting..." : "Submit Feedback"}
                </button>
              </div>
            </form>
        </div>
      </div>
    </div>
  );
}

export default FeedbackModal;

